import { Button, FlexBox, Text } from '@dixa/lib-shared-habla';
import { ChangeEventHandler, useEffect, useRef, useState } from 'react';

import { NotificationType, useNotifications } from 'hooks/useNotifications';

import styles from './submitModal.module.scss';

type Props = {
  change: (field: string, value: string) => void;
};

export function GcmControls({ change }: Props): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [jsonFile, setJsonFile] = useState<File>();
  const [jsonContent, setJsonContent] = useState<string>();
  const notify = useNotifications();

  useEffect(() => {
    if (jsonContent) {
      change('platformCredential', jsonContent);
    }
  }, [jsonContent, change]);

  const onFile: ChangeEventHandler<HTMLInputElement> = async (e) => {
    if (e.currentTarget.files?.length === 0) {
      return;
    }

    const [file] = e.currentTarget.files || [];

    try {
      if (!file) {
        throw new Error('No file to read.');
      }

      if (file.type !== 'application/json') {
        throw new Error('Invalid file type.');
      }

      if (file.size > 1024 * 1024) {
        // This is an arbitrary limit, the file is supposed to be a .json file containing a private key and a few other fields.
        // Normally this file should be a few KBs at most. If it's larger than 1MB, it's very likely not a valid file.
        throw new Error('File size exceeds 1MB.');
      }

      const fileContent = await parseJsonFile(file);
      setJsonFile(file);
      setJsonContent(fileContent);
    } catch (error) {
      setJsonFile(undefined);
      setJsonContent(undefined);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Make sure it is a valid JSON file.';

      notify(
        {
          title: 'Failed to parse file',
          text: errorMessage,
          icon: 'exclamation',
        },
        NotificationType.Error,
      );
    }
  };

  function parseJsonFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject();
      }

      const reader = new FileReader();

      reader.onload = () => {
        resolve(reader.result as string);
      };

      reader.readAsText(file);
    });
  }

  return (
    <FlexBox flexDirection="column" justifyContent="flex-start" gap="300">
      <FlexBox gap="300">
        <Button
          variant="secondary"
          onClick={() => {
            inputRef.current?.click();
          }}
        >
          Choose file
          <input
            className={styles.fileInput}
            ref={inputRef}
            accept="application/json"
            type="file"
            onChange={onFile}
          />
        </Button>

        <FlexBox className={styles.bottomSpacing}>
          {jsonFile ? (
            <Text.Heading className={styles.textSpacing} variant="heading-4">
              {jsonFile.name}
            </Text.Heading>
          ) : null}
        </FlexBox>
      </FlexBox>

      {jsonContent && <pre>{jsonContent}</pre>}
    </FlexBox>
  );
}
